import { getSetting, setSetting } from './settings'
import { getWorkspaceStages } from './workspace'

export const DEFAULT_STAGES = [
  'Cold',
  'Approaching',
  'Active',
  'Meeting Booked',
  'Closed',
  'Do Not Contact',
] as const

/**
 * Returns the pipeline stages for the active business (`workspace_settings.stages`)
 * when it has its own list, otherwise the user's own stages, otherwise the
 * defaults. Mirrors the legacy `getStages()`.
 *
 * Stages are persisted as a JSON array under the `custom_stages` setting.
 * A malformed value, an empty array, or no value at all all collapse to
 * the defaults so the UI can never end up with zero stages.
 */
export function getStages(): string[] {
  const workspaceStages = getWorkspaceStages()
  if (workspaceStages) return [...workspaceStages]
  const raw = getSetting('custom_stages', '')
  if (!raw) return [...DEFAULT_STAGES]
  try {
    const parsed: unknown = JSON.parse(raw)
    if (Array.isArray(parsed) && parsed.length > 0 && parsed.every((s) => typeof s === 'string')) {
      return parsed as string[]
    }
  } catch {
    // fall through to defaults
  }
  return [...DEFAULT_STAGES]
}

/**
 * Persists a new stage list. Empty/whitespace entries are stripped before
 * write so a stray blank from the editor UI can't poison the list.
 */
export async function saveStages(stages: string[]): Promise<void> {
  const cleaned = stages.map((s) => s.trim()).filter((s) => s.length > 0)
  await setSetting('custom_stages', JSON.stringify(cleaned))
}

/** Reset to the defaults by clearing the custom_stages setting. */
export async function resetStages(): Promise<void> {
  await setSetting('custom_stages', '')
}

/**
 * Deal (pipeline) stages. These are NOT the contact lifecycle stages that
 * `getStages()` returns: the legacy Deals board in `crm/index.html` uses
 * its own fixed list (`const STAGES` next to `loadDeals()`), and every row
 * in `deals.stage` was written from it. Mirrored here verbatim so the
 * pipeline island shows exactly the columns the legacy board did. If deal
 * stages ever become configurable, replace this with a getter like
 * `getStages()` and have both host and island read from it.
 */
export const DEAL_STAGES: readonly string[] = [
  'Prospect',
  'Qualified',
  'Demo',
  'Proposal',
  'Negotiation',
  'Closed Won',
  'Closed Lost',
]

/** Column title colours per deal stage (legacy `STAGE_COLORS`). */
export const DEAL_STAGE_COLORS: Readonly<Record<string, string>> = {
  Prospect: 'var(--text-dim)',
  Qualified: 'var(--amber)',
  Demo: 'var(--blue)',
  Proposal: 'var(--purple)',
  Negotiation: 'var(--orange)',
  'Closed Won': 'var(--green)',
  'Closed Lost': 'var(--text-muted)',
}

/** Stages that count as closed: excluded from the pipeline value. */
export const CLOSED_DEAL_STAGES: readonly string[] = ['Closed Won', 'Closed Lost']
